/**
 * THE SHARED COMPONENTS' OWN WORDS, AND WHO TRANSLATES THEM (design 29, W5).
 *
 * web/ui draws a few words of its own: the calendar's arrows, a table's pager, the editor's toolbar,
 * the error and loading states. It must not depend on a translation library - the admin console
 * stays English and loads none - so its components take every word through ONE function from
 * context, `useUiText()`. With no provider it answers from `UI_TEXTS`, the English below, so the
 * admin console is unchanged. The cabinet hands `AppProviders` its catalogue's `t` for the `ui`
 * namespace, whose English file is a copy of `UI_TEXTS` (a cabinet spec holds the two equal).
 *
 * The shapes are i18next's, so the cabinet's `t` drops in: nested keys joined by dots, `{{name}}`
 * placeholders, and a plural as `key_one` / `key_other`, chosen by `count`.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

export const UI_TEXTS = {
  calendar: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    previousYear: 'Previous year',
    nextYear: 'Next year',
    previousDecade: 'Previous decade',
    nextDecade: 'Next decade',
    chooseMonth: 'Choose a month',
    chooseYear: 'Choose a year',
    chooseAnotherMonth: 'Choose another month',
    chooseAnotherYear: 'Choose another year',
  },
  periodPicker: {
    customRange: 'Custom range',
    startDate: 'Start date',
    endDate: 'End date',
    typeADay: 'Type a day up to {{lastDay}}, like {{example}}',
    upToDays_one: 'Up to {{count}} day',
    upToDays_other: 'Up to {{count}} days',
    apply: 'Apply',
  },
  dateTimeField: {
    timesAreIn: 'Times are in {{zone}}.',
    clear: 'Clear',
    hours: 'Hours',
    minutes: 'Minutes',
    done: 'Done',
    suggested: 'Suggested:',
  },
  errorState: {
    title: 'Something went wrong',
    retry: 'Try again',
  },
  loadingState: {
    loading: 'Loading {{what}}.',
  },
  editor: {
    toolbar: 'Formatting',
    paragraph: 'Paragraph',
    heading: 'Heading {{level}}',
    bulletedList: 'Bulleted list',
    numberedList: 'Numbered list',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strikethrough: 'Strikethrough',
    code: 'Code',
    link: 'Link',
    linkGroup: 'The link',
    linkAddress: 'Link address',
    linkAddressHint: 'Starts with https://, http:// or mailto:',
    linkEmpty: 'Type the address these words should open.',
    linkRefused: 'A link must start with https://, http:// or mailto:.',
    applyLink: 'Apply link',
    removeLink: 'Remove link',
    cancel: 'Cancel',
    exitHint:
      'Press Escape to leave the editor and reach the formatting toolbar. Tab and Shift-Tab indent and outdent list items and do not move focus.',
    unreadable:
      'This text could not be opened in the editor. It is kept exactly as it was saved, and nothing here will change it.',
  },
  dataTable: {
    sortAscending: 'Sort by {{column}} ascending',
    sortDescending: 'Sort by {{column}} descending',
    clearSorting: 'Clear sorting on {{column}}',
    loadFailed: 'This list could not be loaded.',
    empty: 'Nothing here yet.',
    loadingRows: 'Loading rows.',
    rowsShown_one: '{{count}} row shown.',
    rowsShown_other: '{{count}} rows shown.',
    resizeHint:
      'Use the left and right arrow keys to resize this column. Hold Shift to move further. Press Home to reset the width.',
    selectAll: 'Select all rows on this page',
    resizeColumn: 'Resize the {{column}} column',
    loading: 'Loading…',
    couldNotLoad: 'Could not load',
    selectRow: 'Select row {{id}}',
    rowsPerPage: 'Rows per page',
    previous: 'Previous',
    next: 'Next',
  },
  dialog: {
    close: 'Close',
    dismiss: 'Dismiss',
  },
} as const;

type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

type WithoutPlural<K extends string> = K extends `${infer Base}_${'one' | 'other'}` ? Base : K;

/** A word web/ui draws: `dataTable.next`. A plural is named without its `_one` / `_other`. */
export type UiTextKey = WithoutPlural<Leaves<typeof UI_TEXTS>>;

export type UiTextValues = Readonly<Record<string, string | number>>;

/** The one function every shared component takes its words through. */
export type UiTranslate = (key: UiTextKey, values?: UiTextValues) => string;

const ENGLISH_PLURALS = new Intl.PluralRules('en');

function lookup(path: string): string | undefined {
  let node: unknown = UI_TEXTS;
  for (const part of path.split('.')) {
    node =
      typeof node === 'object' && node !== null
        ? (node as Record<string, unknown>)[part]
        : undefined;
  }
  return typeof node === 'string' ? node : undefined;
}

/** The English, as i18next would answer it: the plural form `count` takes, placeholders filled. */
export function englishUiText(key: UiTextKey, values: UiTextValues = {}): string {
  const count = values['count'];
  const text =
    (typeof count === 'number' ? lookup(`${key}_${ENGLISH_PLURALS.select(count)}`) : undefined) ??
    lookup(key) ??
    key;
  return text.replace(/\{\{\s*(\w+)\s*\}\}/gu, (placeholder, name: string) => {
    const value = values[name];
    return value === undefined ? placeholder : String(value);
  });
}

const UiTextsContext = createContext<UiTranslate>(englishUiText);

/** Hands the shared components below it their words. `AppProviders` mounts it. */
export function UiTextsProvider({
  translate,
  children,
}: {
  readonly translate: UiTranslate;
  readonly children: ReactNode;
}) {
  return <UiTextsContext value={translate}>{children}</UiTextsContext>;
}

/** The words, in the language the app speaks: English unless a provider says otherwise. */
export function useUiText(): UiTranslate {
  return useContext(UiTextsContext);
}
